import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Tabs,
  Tab,
  Divider,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import LockResetIcon from '@mui/icons-material/LockReset';
import EmailIcon from '@mui/icons-material/Email';
import AssignmentIcon from '@mui/icons-material/Assignment';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PersonIcon from '@mui/icons-material/Person';
import { Contractor, Project, TimeEntry } from '../../../types';
import api from '../../../services/api';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';

const ContractorProfilesTab: React.FC = () => {
  const navigate = useNavigate();
  const [contractors, setContractors] = useState<Contractor[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Contractor | null>(null);
  const [dialogTabValue, setDialogTabValue] = useState(0);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [generatedPassword, setGeneratedPassword] = useState<string | null>(null);
  const [passwordLoading, setPasswordLoading] = useState(false);
  
  // Project Assignment state
  const [projects, setProjects] = useState<Project[]>([]);
  const [assignments, setAssignments] = useState<any[]>([]);
  const [assignmentFormData, setAssignmentFormData] = useState({
    projectId: '',
    monthlyBudgetedHours: '',
  });
  const [assignmentDialogOpen, setAssignmentDialogOpen] = useState(false);
  
  // Hour & Rate Management state
  const [timeEntries, setTimeEntries] = useState<TimeEntry[]>([]);
  const [rateFormData, setRateFormData] = useState({ hourlyRate: '' });
  const [rateDialogOpen, setRateDialogOpen] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    contactEmail: '',
    contactPhone: '',
    hourlyRate: '',
    role: '',
    skillset: '',
    address: '',
    taxId: '',
    paymentTerms: '',
    notes: '',
    isActive: true,
    userRole: 'CONTRACTOR' as 'CONTRACTOR' | 'ADMIN',
    taxDeductionPercentage: '',
  });

  useEffect(() => {
    fetchContractors();
  }, []);

  const fetchContractors = async () => {
    try {
      setLoading(true);
      const response = await api.get('/contractors');
      setContractors(response.data || []);
    } catch (error: any) {
      console.error('Error fetching contractors:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = async (contractor?: Contractor) => {
    if (contractor) {
      setEditing(contractor);
      setFormData({
        name: contractor.name || '',
        contactEmail: contractor.contactEmail || '',
        contactPhone: contractor.contactPhone || '',
        hourlyRate: contractor.hourlyRate?.toString() || '',
        role: '',
        skillset: '',
        address: contractor.address || '',
        taxId: contractor.taxId || '',
        paymentTerms: contractor.paymentTerms || '',
        notes: contractor.notes || '',
        isActive: contractor.isActive ?? true,
        userRole: (contractor.user?.role?.toUpperCase() as 'CONTRACTOR' | 'ADMIN') || 'CONTRACTOR',
        taxDeductionPercentage: (contractor as any).taxDeductionPercentage?.toString() || '',
      });
      setRateFormData({ hourlyRate: contractor.hourlyRate?.toString() || '' });
      // Load related data
      await loadContractorData(contractor.id);
    } else {
      setEditing(null);
      setFormData({
        name: '',
        contactEmail: '',
        contactPhone: '',
        hourlyRate: '',
        role: '',
        skillset: '',
        address: '',
        taxId: '',
        paymentTerms: '',
        notes: '',
        isActive: true,
        userRole: 'CONTRACTOR',
        taxDeductionPercentage: '',
      });
    }
    setDialogTabValue(0);
    setOpen(true);
  };

  const loadContractorData = async (contractorId: string) => {
    try {
      const [projectsRes, assignmentsRes, timeEntriesRes] = await Promise.all([
        api.get('/projects?isActive=true'),
        api.get(`/project-assignments?contractorId=${contractorId}`),
        api.get(`/time-entries?contractorId=${contractorId}&status=PENDING`),
      ]);
      setProjects(projectsRes.data || []);
      setAssignments(assignmentsRes.data || []);
      setTimeEntries(timeEntriesRes.data || []);
    } catch (error: any) {
      console.error('Error loading contractor data:', error);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setEditing(null);
  };

  const handleSubmit = async () => {
    try {
      const data = {
        ...formData,
        hourlyRate: parseFloat(formData.hourlyRate) || 0,
        userRole: formData.userRole, // Include role in update
        taxDeductionPercentage: formData.taxDeductionPercentage ? parseFloat(formData.taxDeductionPercentage) : 0,
      };

      if (editing) {
        await api.put(`/contractors/${editing.id}`, data);
      } else {
        await api.post('/contractors', {
          ...data,
          email: formData.contactEmail, // Use contactEmail as login email for new contractors
        });
      }

      fetchContractors();
      handleClose();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to save contractor');
    }
  };

  const handleToggleActive = async (contractor: Contractor) => {
    try {
      await api.put(`/contractors/${contractor.id}`, {
        isActive: !contractor.isActive,
      });
      fetchContractors();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to update contractor');
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this contractor? This action cannot be undone.')) {
      return;
    }

    try {
      await api.delete(`/contractors/${id}`);
      fetchContractors();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to delete contractor');
    }
  };

  const handleGeneratePassword = async () => {
    if (!editing) return;

    try {
      setPasswordLoading(true);
      const response = await api.post(`/contractors/${editing.id}/generate-password`);
      setGeneratedPassword(response.data.temporaryPassword);
      setPasswordDialogOpen(true);
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to generate password');
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleSendResetEmail = async () => {
    if (!editing) return;

    try {
      setPasswordLoading(true);
      await api.post(`/contractors/${editing.id}/send-reset-email`);
      alert('Password reset email sent successfully to ' + (editing.contactEmail || editing.user?.email || 'the contractor'));
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to send reset email');
    } finally {
      setPasswordLoading(false);
    }
  };

  // Project Assignment handlers
  const handleAssignProject = async () => {
    if (!editing) return;
    try {
      await api.post('/project-assignments', {
        contractorId: editing.id,
        projectId: assignmentFormData.projectId,
        monthlyBudgetedHours: assignmentFormData.monthlyBudgetedHours ? parseFloat(assignmentFormData.monthlyBudgetedHours) : null,
      });
      await loadContractorData(editing.id);
      setAssignmentDialogOpen(false);
      setAssignmentFormData({ projectId: '', monthlyBudgetedHours: '' });
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to assign project');
    }
  };

  const handleRemoveAssignment = async (projectId: string) => {
    if (!editing) return;
    if (!window.confirm('Are you sure you want to remove this assignment?')) return;
    try {
      await api.delete(`/project-assignments/${editing.id}/${projectId}`);
      await loadContractorData(editing.id);
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to remove assignment');
    }
  };

  // Hour & Rate Management handlers
  const handleUpdateRate = async () => {
    if (!editing) return;
    try {
      await api.put(`/contractors/${editing.id}`, {
        hourlyRate: parseFloat(rateFormData.hourlyRate),
      });
      fetchContractors();
      setRateDialogOpen(false);
      alert('Hourly rate updated successfully');
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to update rate');
    }
  };

  const handleApproveHours = async (entryId: string) => {
    try {
      await api.put(`/time-entries/${entryId}`, { status: 'APPROVED' });
      if (editing) await loadContractorData(editing.id);
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to approve hours');
    }
  };

  const handleRejectHours = async (entryId: string) => {
    try {
      await api.put(`/time-entries/${entryId}`, { status: 'REJECTED' });
      if (editing) await loadContractorData(editing.id);
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to reject hours');
    }
  };

  interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
  }

  const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
    return (
      <div role="tabpanel" hidden={value !== index}>
        {value === index && <Box sx={{ py: 2 }}>{children}</Box>}
      </div>
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">Contractor Profiles</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpen()}>
          Create Contractor Profile
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Contact Email</TableCell>
              <TableCell>Contact Phone</TableCell>
              <TableCell>Hourly Rate</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {contractors.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography color="text.secondary">No contractors found</Typography>
                </TableCell>
              </TableRow>
            ) : (
              contractors.map((contractor) => (
                <TableRow key={contractor.id}>
                  <TableCell>
                    <Typography
                      component="button"
                      onClick={() => navigate(`/contractors/${contractor.id}`)}
                      sx={{
                        color: 'primary.main',
                        cursor: 'pointer',
                        textDecoration: 'none',
                        border: 'none',
                        background: 'none',
                        padding: 0,
                        font: 'inherit',
                        '&:hover': {
                          textDecoration: 'underline',
                        },
                      }}
                    >
                      {contractor.name}
                    </Typography>
                  </TableCell>
                  <TableCell>{contractor.contactEmail || '-'}</TableCell>
                  <TableCell>{contractor.contactPhone || '-'}</TableCell>
                  <TableCell>${contractor.hourlyRate.toFixed(2)}/hr</TableCell>
                  <TableCell>
                    <Chip
                      label={contractor.isActive ? 'Active' : 'Inactive'}
                      color={contractor.isActive ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => handleOpen(contractor)}>
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={contractor.isActive ? 'Deactivate' : 'Activate'}>
                      <IconButton
                        size="small"
                        onClick={() => handleToggleActive(contractor)}
                        color={contractor.isActive ? 'default' : 'success'}
                      >
                        {contractor.isActive ? <BlockIcon /> : <CheckCircleIcon />}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" color="error" onClick={() => handleDelete(contractor.id)}>
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
        <DialogTitle>{editing ? 'Edit Contractor Profile' : 'Create Contractor Profile'}</DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {editing ? (
            <Box>
              <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
                <Tabs value={dialogTabValue} onChange={(e, newValue) => setDialogTabValue(newValue)}>
                  <Tab icon={<PersonIcon />} iconPosition="start" label="Profile" />
                  <Tab icon={<AssignmentIcon />} iconPosition="start" label="Project Assignment" />
                  <Tab icon={<AccessTimeIcon />} iconPosition="start" label="Hour & Rate Management" />
                  <Tab icon={<LockResetIcon />} iconPosition="start" label="Account & Access" />
                </Tabs>
              </Box>
              
              <Box sx={{ p: 3, maxHeight: '70vh', overflow: 'auto' }}>
                {/* Profile Tab */}
                <TabPanel value={dialogTabValue} index={0}>
                  <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Name *"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Contact Email"
                  type="email"
                  value={formData.contactEmail}
                  onChange={(e) => setFormData({ ...formData, contactEmail: e.target.value })}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Contact Phone"
                  value={formData.contactPhone}
                  onChange={(e) => setFormData({ ...formData, contactPhone: e.target.value })}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Hourly Rate *"
                  type="number"
                  value={formData.hourlyRate}
                  onChange={(e) => setFormData({ ...formData, hourlyRate: e.target.value })}
                  fullWidth
                  required
                  inputProps={{ step: '0.01', min: '0' }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Tax Deduction Percentage"
                  type="number"
                  value={formData.taxDeductionPercentage || ''}
                  onChange={(e) => setFormData({ ...formData, taxDeductionPercentage: e.target.value })}
                  fullWidth
                  inputProps={{ step: '0.01', min: '0', max: '100' }}
                  helperText="Percentage to deduct from invoice total (e.g., 7.5 for 7.5%)"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Role"
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Skillset"
                  value={formData.skillset}
                  onChange={(e) => setFormData({ ...formData, skillset: e.target.value })}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Address"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  fullWidth
                  multiline
                  rows={2}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Tax ID"
                  value={formData.taxId}
                  onChange={(e) => setFormData({ ...formData, taxId: e.target.value })}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Payment Terms"
                  value={formData.paymentTerms}
                  onChange={(e) => setFormData({ ...formData, paymentTerms: e.target.value })}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  fullWidth
                  multiline
                  rows={3}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isActive}
                      onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    />
                  }
                  label="Active"
                />
              </Grid>
              {editing && (
                <Grid item xs={12}>
                  <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 2, p: 2, bgcolor: '#f9f9f9' }}>
                    <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, mb: 1 }}>
                      Access Role
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                      Determines which portal the user can access. Contractors access the Contractor Portal, Administrators access the Administrator Portal with full edit functions.
                    </Typography>
                    <FormControl fullWidth>
                      <InputLabel>User Role</InputLabel>
                      <Select
                        value={formData.userRole}
                        label="User Role"
                        onChange={(e) => setFormData({ ...formData, userRole: e.target.value as 'CONTRACTOR' | 'ADMIN' })}
                      >
                        <MenuItem value="CONTRACTOR">Contractor (Default - Contractor Portal)</MenuItem>
                        <MenuItem value="ADMIN">Administrator (Administrator Portal with Edit Functions)</MenuItem>
                      </Select>
                    </FormControl>
                    <Alert severity="info" sx={{ mt: 2 }}>
                      <Typography variant="caption">
                        <strong>Note:</strong> Changing the role will affect which portal the user can access. Each user has an independent password for security.
                      </Typography>
                    </Alert>
                  </Box>
                </Grid>
              )}
                    <Grid item xs={12}>
                      <Box sx={{ border: '2px dashed #ccc', borderRadius: 2, p: 2, textAlign: 'center' }}>
                        <UploadFileIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Upload Documents
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Contracts, Certifications, NDAs (Upload functionality coming soon)
                        </Typography>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<UploadFileIcon />}
                          sx={{ mt: 1 }}
                          disabled
                        >
                          Select Files
                        </Button>
                      </Box>
                    </Grid>
                  </Grid>
                </TabPanel>

                {/* Project Assignment Tab */}
                <TabPanel value={dialogTabValue} index={1}>
                  <Box>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                      <Typography variant="h6">Project Assignments</Typography>
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() => setAssignmentDialogOpen(true)}
                      >
                        Assign to Project
                      </Button>
                    </Box>
                    {assignments.length === 0 ? (
                      <Alert severity="info">No project assignments found.</Alert>
                    ) : (
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Project Name</TableCell>
                              <TableCell align="right">Budgeted Hours</TableCell>
                              <TableCell align="right">Actions</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {assignments.map((assignment) => (
                              <TableRow key={assignment.id}>
                                <TableCell>{assignment.project?.name || '-'}</TableCell>
                                <TableCell align="right">
                                  {assignment.monthlyBudgetedHours ? assignment.monthlyBudgetedHours.toFixed(2) : '-'}
                                </TableCell>
                                <TableCell align="right">
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => handleRemoveAssignment(assignment.projectId)}
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </Box>
                </TabPanel>

                {/* Hour & Rate Management Tab */}
                <TabPanel value={dialogTabValue} index={2}>
                  <Box>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                      <Typography variant="h6">Hourly Rate</Typography>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<EditIcon />}
                        onClick={() => setRateDialogOpen(true)}
                      >
                        Update Rate
                      </Button>
                    </Box>
                    <Alert severity="info" sx={{ mb: 2 }}>
                      Current Rate: ${editing?.hourlyRate.toFixed(2)}/hr
                    </Alert>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="h6" gutterBottom>
                      Pending Time Entries
                    </Typography>
                    {timeEntries.length === 0 ? (
                      <Alert severity="info">No pending time entries.</Alert>
                    ) : (
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Date</TableCell>
                              <TableCell>Project</TableCell>
                              <TableCell align="right">Hours</TableCell>
                              <TableCell>Description</TableCell>
                              <TableCell align="right">Actions</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {timeEntries.map((entry) => (
                              <TableRow key={entry.id}>
                                <TableCell>{format(new Date(entry.dateWorked), 'MMM dd, yyyy')}</TableCell>
                                <TableCell>{entry.project?.name || '-'}</TableCell>
                                <TableCell align="right">{entry.hoursWorked.toFixed(2)}</TableCell>
                                <TableCell>
                                  <Typography variant="body2" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {entry.description}
                                  </Typography>
                                </TableCell>
                                <TableCell align="right">
                                  <Tooltip title="Approve">
                                    <IconButton
                                      size="small"
                                      color="success"
                                      onClick={() => handleApproveHours(entry.id)}
                                    >
                                      <CheckCircleIcon />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Reject">
                                    <IconButton
                                      size="small"
                                      color="error"
                                      onClick={() => handleRejectHours(entry.id)}
                                    >
                                      <CancelIcon />
                                    </IconButton>
                                  </Tooltip>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </Box>
                </TabPanel>

                {/* Account & Access Tab */}
                <TabPanel value={dialogTabValue} index={3}>
                  <Box>
                    <Typography variant="h6" gutterBottom>
                      Account & Access Control
                    </Typography>
                    <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 2, p: 2, bgcolor: '#f5f5f5', mb: 2 }}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Login Email: {editing?.user?.email || editing?.contactEmail || 'No email'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Status: <Chip label={editing?.isActive ? 'Active' : 'Inactive'} color={editing?.isActive ? 'success' : 'default'} size="small" />
                      </Typography>
                    </Box>
                    <Box display="flex" gap={2} flexWrap="wrap">
                      <Button
                        variant="outlined"
                        startIcon={<LockResetIcon />}
                        onClick={handleGeneratePassword}
                        disabled={passwordLoading}
                      >
                        Generate New Password
                      </Button>
                      <Button
                        variant="outlined"
                        startIcon={<EmailIcon />}
                        onClick={handleSendResetEmail}
                        disabled={passwordLoading}
                      >
                        Send Reset Email
                      </Button>
                    </Box>
                  </Box>
                </TabPanel>
              </Box>
            </Box>
            ) : (
              // Create mode - show only profile form
              <Box sx={{ p: 3 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Name *"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      fullWidth
                      required
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Contact Email"
                      type="email"
                      value={formData.contactEmail}
                      onChange={(e) => setFormData({ ...formData, contactEmail: e.target.value })}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Contact Phone"
                      value={formData.contactPhone}
                      onChange={(e) => setFormData({ ...formData, contactPhone: e.target.value })}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Hourly Rate *"
                      type="number"
                      value={formData.hourlyRate}
                      onChange={(e) => setFormData({ ...formData, hourlyRate: e.target.value })}
                      fullWidth
                      required
                      inputProps={{ step: '0.01', min: '0' }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.isActive}
                          onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                        />
                      }
                      label="Active"
                    />
                  </Grid>
                </Grid>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>Cancel</Button>
            <Button onClick={handleSubmit} variant="contained" disabled={!formData.name || !formData.hourlyRate}>
              {editing ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Project Assignment Dialog */}
        <Dialog open={assignmentDialogOpen} onClose={() => setAssignmentDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Assign to Project</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <FormControl fullWidth required>
                <InputLabel>Project</InputLabel>
                <Select
                  value={assignmentFormData.projectId}
                  label="Project"
                  onChange={(e) => setAssignmentFormData({ ...assignmentFormData, projectId: e.target.value })}
                >
                  {projects
                    .filter(p => !assignments.find(a => a.projectId === p.id))
                    .map((project) => (
                      <MenuItem key={project.id} value={project.id}>
                        {project.name}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
              <TextField
                label="Monthly Budgeted Hours (optional)"
                type="number"
                value={assignmentFormData.monthlyBudgetedHours}
                onChange={(e) => setAssignmentFormData({ ...assignmentFormData, monthlyBudgetedHours: e.target.value })}
                fullWidth
                inputProps={{ step: '0.01', min: '0' }}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAssignmentDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={handleAssignProject}
              variant="contained"
              disabled={!assignmentFormData.projectId}
            >
              Assign
            </Button>
          </DialogActions>
        </Dialog>

        {/* Update Rate Dialog */}
        <Dialog open={rateDialogOpen} onClose={() => setRateDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Update Hourly Rate</DialogTitle>
          <DialogContent>
            <TextField
              label="Hourly Rate *"
              type="number"
              value={rateFormData.hourlyRate}
              onChange={(e) => setRateFormData({ hourlyRate: e.target.value })}
              fullWidth
              required
              inputProps={{ step: '0.01', min: '0' }}
              sx={{ mt: 2 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setRateDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={handleUpdateRate}
              variant="contained"
              disabled={!rateFormData.hourlyRate}
            >
              Update
            </Button>
          </DialogActions>
        </Dialog>

      {/* Password Display Dialog */}
      <Dialog open={passwordDialogOpen} onClose={() => setPasswordDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>New Password Generated</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2" fontWeight="bold">
              ⚠️ IMPORTANT: Save this password immediately. It will not be shown again.
            </Typography>
          </Alert>
          <TextField
            label="Temporary Password"
            value={generatedPassword || ''}
            fullWidth
            InputProps={{
              readOnly: true,
            }}
            sx={{ mt: 2 }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Share this password securely with the contractor. They should change it after first login.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              if (generatedPassword) {
                navigator.clipboard.writeText(generatedPassword);
                alert('Password copied to clipboard');
              }
            }}
          >
            Copy Password
          </Button>
          <Button onClick={() => setPasswordDialogOpen(false)} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ContractorProfilesTab;

